import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "@headlessui/react";
import { createPortal } from "react-dom";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CloudDownloadIcon,
  CloudUploadIcon,
  PhotographIcon,
  TrashIcon,
  XIcon,
} from "@heroicons/react/outline";
import { toast } from "react-toastify";
import JSZip from "jszip";
import * as api from "./apiService";
import * as imageUtils from "../../imageUtils";
import ImageNotAvailable from "../../images/img_not_available.jpg";

function uploaderName(photo) {
  return photo.uploaded_by_name || photo.uploaded_by?.full_name || (photo.customer_uploaded ? "Customer" : "LTO team");
}

function normalizePhoto(photo) {
  return {
    ...photo,
    image: imageUtils.normalizeCloudinaryUrl(photo.image),
    thumbnail: photo.thumbnail || imageUtils.normalizeCloudinaryUrl(photo.image),
    preview: photo.preview || imageUtils.normalizeCloudinaryUrl(photo.image),
  };
}

function uniqueZipFilename(name, photoId, usedNames) {
  if (!usedNames.has(name)) {
    usedNames.add(name);
    return name;
  }

  const extensionIndex = name.lastIndexOf(".");
  const base = extensionIndex > 0 ? name.slice(0, extensionIndex) : name;
  const extension = extensionIndex > 0 ? name.slice(extensionIndex) : "";
  const uniqueName = `${base}-${photoId}${extension}`;
  usedNames.add(uniqueName);
  return uniqueName;
}

export default function JobPhotosWorkspace({ jobId, currentUser, jobStatus, onCountChange, toolbarTarget }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [error, setError] = useState("");
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [previewImageLoaded, setPreviewImageLoaded] = useState(false);
  const fileInputRef = useRef(null);

  const canUpload = !currentUser.isCustomer;
  const canSeeUploader = currentUser.isAdmin || currentUser.isSuperUser || currentUser.isInternalCoordinator;
  const isProjectManager = currentUser.isProjectManager || currentUser.isExternalProjectManager;
  const canDelete =
    currentUser.isAdmin ||
    currentUser.isSuperUser ||
    currentUser.isAccountManager ||
    (currentUser.isInternalCoordinator && ["A", "S", "U", "W", "T", "C"].includes(jobStatus)) ||
    (!currentUser.isInternalCoordinator &&
      isProjectManager &&
      !["C", "I"].includes(jobStatus));

  const sortedPhotos = useMemo(
    () => [...photos].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [photos],
  );

  const getPhotos = useCallback(async ({ showLoader = true, refresh = false } = {}) => {
    if (showLoader) setLoading(true);
    setError("");
    try {
      const { data } = await api.getJobPhotos(jobId, refresh);
      setPhotos((data.results || []).map(normalizePhoto));
    } catch (requestError) {
      setError(requestError.response?.status === 403
        ? "You do not have permission to view photos for this job."
        : "Unable to load job photos.");
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    getPhotos();
  }, [getPhotos]);

  useEffect(() => {
    if (!loading && !error) onCountChange?.(photos.length);
  }, [error, loading, onCountChange, photos.length]);

  const chooseFiles = () => fileInputRef.current?.click();

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((file) => file.type.startsWith("image/") && file.size <= 10000000);
    if (files.length === 0) {
      toast.error("Choose JPG, PNG, or GIF images up to 10 MB each.");
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("photo", file));

    setUploading(true);
    try {
      const { data } = await api.uploadPhotos(jobId, formData);
      toast.success(`${data.uploaded_photos} photo${data.uploaded_photos === 1 ? "" : "s"} uploaded.`);
      const uploadedPhotos = (data.photos || []).map(normalizePhoto);
      if (uploadedPhotos.length > 0) {
        const uploadedIds = new Set(uploadedPhotos.map((photo) => photo.id));
        setPhotos((current) => [
          ...uploadedPhotos,
          ...current.filter((photo) => !uploadedIds.has(photo.id)),
        ]);
      } else {
        // Older API deployments do not return the new photo records. Bypass any
        // cached listing response so the uploaded photos still appear immediately.
        await getPhotos({ showLoader: false, refresh: true });
      }
    } catch (requestError) {
      toast.error(requestError.response?.status === 406
        ? "This job has reached its photo limit."
        : "Unable to upload photos at this time.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadPhoto = async (photo) => {
    try {
      const { data } = await api.downloadJobPhoto(jobId, photo.id);
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = photo.name || "job-photo";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      toast.error("Unable to download this photo.");
    }
  };

  const downloadAllPhotos = async () => {
    if (photos.length === 0 || downloadingAll) return;

    setDownloadingAll(true);
    try {
      const downloadedPhotos = await Promise.all(sortedPhotos.map(async (photo) => {
        const { data } = await api.downloadJobPhoto(jobId, photo.id);
        return { blob: data, id: photo.id, name: photo.name || `job-photo-${photo.id}` };
      }));
      const zip = new JSZip();
      const usedNames = new Set();
      downloadedPhotos.forEach(({ blob, id, name }) => {
        zip.file(uniqueZipFilename(name, id, usedNames), blob);
      });
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `job-${jobId}-photos.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`${downloadedPhotos.length} photo${downloadedPhotos.length === 1 ? "" : "s"} downloaded.`);
    } catch (downloadError) {
      toast.error("Unable to download all photos.");
    } finally {
      setDownloadingAll(false);
    }
  };

  const deletePhotoIds = async (photoIds) => {
    try {
      await api.deletePhotos(jobId, { photos: photoIds });
      setPhotos((current) => current.filter((photo) => !photoIds.includes(photo.id)));
      toast.success(`${photoIds.length === 1 ? "Photo" : "Photos"} deleted.`);
    } catch (deleteError) {
      toast.error("Unable to delete the selected photos.");
    }
  };

  const openPreview = (photo) => {
    setPreviewImageLoaded(false);
    setPreviewPhoto(photo);
  };

  const navigatePreview = (direction) => {
    const currentIndex = sortedPhotos.findIndex((photo) => photo.id === previewPhoto?.id);
    if (currentIndex === -1 || sortedPhotos.length < 2) return;
    const nextIndex = (currentIndex + direction + sortedPhotos.length) % sortedPhotos.length;
    setPreviewImageLoaded(false);
    setPreviewPhoto(sortedPhotos[nextIndex]);
  };

  useEffect(() => {
    if (!previewPhoto) return undefined;

    const previewUrl = previewPhoto.preview || previewPhoto.image;
    const image = new Image();
    let cancelled = false;
    image.onload = () => {
      if (!cancelled) setPreviewImageLoaded(true);
    };
    image.onerror = () => {
      if (!cancelled) setPreviewImageLoaded(false);
    };
    image.src = previewUrl;

    const currentIndex = sortedPhotos.findIndex((photo) => photo.id === previewPhoto.id);
    [-1, 1].forEach((offset) => {
      if (sortedPhotos.length < 2 || currentIndex === -1) return;
      const adjacent = sortedPhotos[(currentIndex + offset + sortedPhotos.length) % sortedPhotos.length];
      const preload = new Image();
      preload.src = adjacent.preview || adjacent.image;
    });

    return () => {
      cancelled = true;
    };
  }, [previewPhoto, sortedPhotos]);

  if (loading) {
    return (
      <div className="grid gap-4 p-5 sm:grid-cols-2" aria-label="Loading job photos">
        {[1, 2, 3, 4].map((item) => <div key={item} className="h-52 animate-pulse rounded-xl bg-slate-100" />)}
      </div>
    );
  }

  if (error) return <div className="m-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>;

  const toolbar = (
    <div className="flex items-center gap-2">
      {photos.length > 0 && (
        <button
          type="button"
          onClick={downloadAllPhotos}
          disabled={downloadingAll}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
        >
          <CloudDownloadIcon className={`h-4 w-4 ${downloadingAll ? "animate-pulse" : ""}`} aria-hidden="true" />
          {downloadingAll ? "Preparing download…" : "Download all"}
        </button>
      )}
      {canUpload && (
        <>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif" multiple onChange={(event) => uploadFiles(event.target.files)} className="sr-only" aria-label="Choose photos" />
          <button type="button" onClick={chooseFiles} disabled={uploading} className="inline-flex items-center gap-1.5 rounded-lg bg-[#E3222A] px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-wait disabled:opacity-60">
            <CloudUploadIcon className={`h-4 w-4 ${uploading ? "animate-pulse" : ""}`} aria-hidden="true" />
            {uploading ? "Uploading…" : "Upload photos"}
          </button>
        </>
      )}
    </div>
  );

  return (
    <>
      {toolbarTarget && createPortal(toolbar, toolbarTarget)}
      <div className="bg-[#F8FAFC]">
      <div className="p-5">
        <section aria-label={`${sortedPhotos.length} job photos`}>
          {sortedPhotos.length === 0 ? (
            <div className="grid min-h-[210px] place-items-center rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center">
              <div>
                <PhotographIcon className="mx-auto h-9 w-9 text-slate-300" aria-hidden="true" />
                <p className="mt-3 text-sm font-bold text-slate-700">No photos</p>
                <p className="mt-1 text-xs text-slate-500">Uploaded photos will appear here.</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
              {sortedPhotos.map((photo) => (
                  <article key={photo.id} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <button
                      type="button"
                      onClick={() => openPreview(photo)}
                      className="relative block h-24 w-full bg-slate-100 text-left sm:h-28"
                      aria-label={`Preview ${photo.name || "job photo"}`}
                    >
                      <img src={photo.thumbnail || photo.image || ImageNotAvailable} alt={photo.name || "Job photo"} onError={(event) => { event.currentTarget.src = ImageNotAvailable; }} className="h-full w-full object-cover" />
                    </button>
                    <div className="p-2">
                      {canSeeUploader && (
                        <p className="w-full break-words text-[10px] leading-4 text-slate-500">
                          Added by {photo.customer_uploaded ? (
                            <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">
                              Customer
                            </span>
                          ) : (
                            <strong className="font-semibold text-slate-700">{uploaderName(photo)}</strong>
                          )}
                        </p>
                      )}
                      <div data-testid={`photo-actions-${photo.id}`} className="mt-1.5 flex items-center justify-end gap-1">
                        <button type="button" onClick={() => downloadPhoto(photo)} className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100" aria-label={`Download ${photo.name}`} title="Download"><CloudDownloadIcon className="h-4 w-4" /></button>
                        {canDelete && <button type="button" onClick={() => deletePhotoIds([photo.id])} className="rounded-md p-1.5 text-[#E3222A] hover:bg-red-50" aria-label={`Delete ${photo.name}`} title="Delete"><TrashIcon className="h-4 w-4" /></button>}
                      </div>
                    </div>
                  </article>
              ))}
            </div>
          )}
        </section>
      </div>

      {previewPhoto && (
        <Dialog open as="div" className="relative z-[11000]" onClose={() => setPreviewPhoto(null)}>
          <div className="fixed inset-0 bg-slate-950/80" />
          <div className="fixed inset-0 overflow-y-auto p-4 sm:p-8">
            <div className="flex min-h-full items-center justify-center">
              <Dialog.Panel
                className="relative max-w-6xl"
                onKeyDown={(event) => {
                  if (event.key === "ArrowLeft") navigatePreview(-1);
                  if (event.key === "ArrowRight") navigatePreview(1);
                }}
              >
                <Dialog.Title className="sr-only">{previewPhoto?.name || "Job photo preview"}</Dialog.Title>
                <button type="button" onClick={() => setPreviewPhoto(null)} className="absolute -right-2 -top-2 z-10 grid h-9 w-9 place-items-center rounded-full bg-white text-slate-900 shadow-lg" aria-label="Close photo preview"><XIcon className="h-5 w-5" /></button>
                {sortedPhotos.length > 1 && (
                  <>
                    <button type="button" onClick={() => navigatePreview(-1)} className="absolute left-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/95 text-slate-900 shadow-lg hover:bg-white sm:-left-14" aria-label="Previous photo"><ChevronLeftIcon className="h-6 w-6" /></button>
                    <button type="button" onClick={() => navigatePreview(1)} className="absolute right-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/95 text-slate-900 shadow-lg hover:bg-white sm:-right-14" aria-label="Next photo"><ChevronRightIcon className="h-6 w-6" /></button>
                  </>
                )}
                <img
                  src={(previewImageLoaded ? previewPhoto.preview : previewPhoto.thumbnail) || previewPhoto.image || ImageNotAvailable}
                  alt={previewPhoto.name || "Job photo"}
                  className="max-h-[88vh] max-w-full rounded-xl object-contain shadow-2xl"
                />
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      )}
      </div>
    </>
  );
}
